import React, { useMemo } from "react"
import { DateTime } from "luxon"
import i18n from "@/i18n"
import { getWeekdayHours, type OpeningHour } from "@/utils/opening_hours"

interface VenueOpeningHoursProps {
  openingHours: OpeningHour[]
  timezone: string
}

const WEEKDAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]

const VenueOpeningHours: React.FC<VenueOpeningHoursProps> = ({ openingHours, timezone }) => {
  const now = useMemo(() => DateTime.now().setZone(timezone), [timezone])

  const weekHours = useMemo(() => {
    const startOfWeek = now.startOf("week")

    return Array.from({ length: 7 }, (_, i) => {
      const date = startOfWeek.plus({ days: i })
      const weekday = date.weekday % 7
      const hours = getWeekdayHours(openingHours, weekday, date)
      const isToday = date.hasSame(now, "day")

      return {
        weekday,
        date,
        hours,
        isToday,
        label: i18n.t(`app.date.weekdays.${WEEKDAY_NAMES[weekday]}`),
      }
    })
  }, [openingHours, timezone, now])

  return (
    <div id="opening-hours" className="mb-8" data-testid="public-venue-hours-container">
      <h2 className="text-base font-medium mb-4 mt-0">
        <a href="#opening-hours" className="text-[#222] no-underline">
          {i18n.t("app.public.venue.opening_hours.title")}
        </a>
      </h2>
      <div
        className="rounded-[5px] border border-[#f1f2f5] bg-white overflow-hidden"
        data-testid="public-venue-hours-list"
      >
        {weekHours.map(({ weekday, label, hours, isToday }, index) => {
          const isLast = index === weekHours.length - 1
          const timeText =
            hours?.closed || !hours?.startTime || !hours?.endTime
              ? i18n.t("app.public.venue.opening_hours.closed")
              : `${hours.startTime} - ${hours.endTime}`

          return (
            <div
              key={weekday}
              className={`flex items-center justify-between px-5 py-3.5 text-sm${
                isToday ? " bg-[#f4f9ff]" : ""
              }${!isLast ? " border-b border-[#f1f2f5]" : ""}`}
              data-testid={`public-venue-hours-day-${weekday}`}
            >
              <div className="flex items-center gap-2">
                <span className={isToday ? "font-semibold text-[#2596fa]" : "font-medium text-[#121724]"}>{label}</span>
                {isToday && (
                  <span className="inline-flex items-center justify-center rounded-[5px] bg-[#2596fa] px-1.5 py-0.5 text-[10px] font-medium leading-tight text-white">
                    {i18n.t("app.public.venue.opening_hours.today")}
                  </span>
                )}
              </div>
              <span className="font-normal text-[#535d6a]">{timeText}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default VenueOpeningHours
